import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Link, Trash2, GripVertical, Plus, X, Save } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

const categories = [
  { id: 'varanasi', label: 'Varanasi & Surroundings' },
  { id: 'deluxe', label: 'Deluxe Rooms' },
  { id: 'premium', label: 'Standard Room' },
  { id: 'ultra-large', label: 'Ultra-Large Room' }
];

const AdminGallery = () => {
  const [selectedCategory, setSelectedCategory] = useState('varanasi');
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [captionInput, setCaptionInput] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchImages();
  }, [selectedCategory]);

  const fetchImages = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/gallery?category=${selectedCategory}`);
      setImages(response.data);
    } catch (error) {
      toast.error('Failed to load images');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setIsUploading(true);
    try {
      for (const file of files) {
        // Upload to Cloudinary directly from frontend
        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
        formData.append('folder', 'ved_homestays');

        const cloudinaryResponse = await axios.post(
          `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
          formData
        );

        const { secure_url, public_id } = cloudinaryResponse.data;

        // Save to backend
        await axios.post(
          `${BACKEND_URL}/api/admin/gallery/url`,
          {
            url: secure_url,
            public_id: public_id,
            caption: file.name.replace(/\.[^/.]+$/, ''),
            category: selectedCategory,
            order: images.length
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      toast.success(`${files.length} image(s) uploaded successfully!`);
      fetchImages();
    } catch (error) {
      toast.error('Failed to upload image');
      console.error(error);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAddUrl = async () => {
    if (!urlInput.trim()) return;

    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/gallery/url`,
        {
          url: urlInput.trim(),
          caption: captionInput.trim(),
          category: selectedCategory,
          order: images.length
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Image added successfully!');
      setShowUrlModal(false);
      setUrlInput('');
      setCaptionInput('');
      fetchImages();
    } catch (error) {
      toast.error('Failed to add image');
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/gallery/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Image deleted');
      fetchImages();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleCaptionUpdate = async (imageId, newCaption) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/gallery/${imageId}`,
        { caption: newCaption },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Caption updated');
    } catch (error) {
      toast.error('Failed to update caption');
    }
  };

  const handleDragStart = (index) => setDraggedIndex(index);

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newImages = [...images];
    const dragged = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(index, 0, dragged);
    setImages(newImages);
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    setDraggedIndex(null);
    try {
      const imageOrders = images.map((img, index) => ({ id: img.id, order: index }));
      await axios.post(
        `${BACKEND_URL}/api/admin/gallery/reorder`,
        imageOrders,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order saved!');
    } catch (error) {
      toast.error('Failed to save order');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Gallery Management</h1>
            <p className="text-gray-500 text-sm mt-1">Upload and manage photos for your website</p>
          </div>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border rounded-lg"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Category Tabs */}
        <div className="flex gap-2 mb-8 flex-wrap">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedCategory === cat.id
                  ? 'bg-orange-600 text-white'
                  : 'bg-white text-gray-600 border hover:border-orange-400'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mb-8">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept="image/*"
            multiple
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="flex items-center gap-2 px-5 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 font-medium"
          >
            <Upload size={18} />
            {isUploading ? 'Uploading...' : 'Upload Photos'}
          </button>
          <button
            onClick={() => setShowUrlModal(true)}
            className="flex items-center gap-2 px-5 py-3 bg-white border-2 border-orange-600 text-orange-600 rounded-lg hover:bg-orange-50 font-medium"
          >
            <Link size={18} />
            Add by URL
          </button>
        </div>

        {/* Images Grid */}
        {isLoading ? (
          <div className="text-center py-20">
            <div className="inline-block w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-500 mt-4">Loading images...</p>
          </div>
        ) : images.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border-2 border-dashed border-gray-300">
            <Upload size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500 text-lg">No images yet for this category</p>
            <p className="text-gray-400 text-sm mt-2">Upload photos or add by URL</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-4">
              💡 Drag and drop to reorder images
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {images.map((image, index) => (
                <div
                  key={image.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`bg-white rounded-xl shadow-md overflow-hidden ${
                    draggedIndex === index ? 'opacity-50' : ''
                  }`}
                >
                  <div className="relative aspect-[4/3]">
                    <img
                      src={image.url}
                      alt={image.caption}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 left-2 bg-black/50 rounded-lg p-1 cursor-grab">
                      <GripVertical size={16} className="text-white" />
                    </div>
                    <button
                      onClick={() => handleDelete(image.id)}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-lg p-1.5 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="p-3 flex gap-2">
                    <input
                      type="text"
                      defaultValue={image.caption}
                      placeholder="Add caption..."
                      className="flex-1 text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      onBlur={(e) => handleCaptionUpdate(image.id, e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* URL Modal */}
      {showUrlModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Add Image by URL</h3>
              <button onClick={() => setShowUrlModal(false)}>
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                <input
                  type="url"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="w-full border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Caption (optional)</label>
                <input
                  type="text"
                  value={captionInput}
                  onChange={(e) => setCaptionInput(e.target.value)}
                  placeholder="Describe the image..."
                  className="w-full border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowUrlModal(false)}
                  className="flex-1 px-4 py-3 border rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddUrl}
                  className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
                >
                  Add Image
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGallery;
